import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

// Third-party
import clsx from 'clsx';

// Hooks
import { useMessageFilter } from '@/utils/hooks/conversations/useMessageFilter';
import { useMessages } from '@/utils/hooks/conversations/useMessages';

// Components
import { EmptyState } from '@/components/base/EmptyState';
import LoadMoreInfinite from '@/components/base/LoadMoreInfinite/LoadMoreInfinite';
import { MessageSkeleton, SocialInboxMessage } from '@/components/pages/conversations/SocialInbox';
// Constants/Libs/Types
import { MESSAGE_FILTER_META } from '@/components/pages/conversations/SocialInbox/const';
import MentionMediaViewerModal from '@/components/pages/listening/widget/MentionMediaViewerModal';

export const ConversationMessageList = ({ className }: { className?: string }) => {
    const { t } = useTranslation('conversations');
    const { messageFilter } = useMessageFilter();
    const { filteredMessages, isLoading, isLoadingMore, isLoadMore, loadMore } = useMessages();

    const [selectedMediaId, setSelectedMediaId] = useState<string | null>(null);

    const handlePostClick = (mediaId: string) => {
        if (messageFilter === MESSAGE_FILTER_META.mention.key) {
            setSelectedMediaId(mediaId);
        }
    };

    const renderMediaModal = () => {
        if (messageFilter === MESSAGE_FILTER_META.mention.key) {
            return (
                <MentionMediaViewerModal
                    isOpen={Boolean(selectedMediaId)}
                    toggle={() => setSelectedMediaId(null)}
                    mediaId={selectedMediaId}
                />
            );
        }
    };

    const renderMessages = () => {
        if (isLoading) {
            return [...Array(5).keys()].map((index) => <MessageSkeleton key={index} />);
        }

        if (!filteredMessages.length) {
            return (
                <EmptyState
                    icon={MESSAGE_FILTER_META[messageFilter].icon}
                    size="sm"
                    title={t('empty_state.no_content.title', {
                        item: t(`tabs.${messageFilter}`).toLowerCase()
                    })}
                    description={t('empty_state.no_content.description')}
                />
            );
        }

        return (
            <>
                {filteredMessages.map((message) => (
                    <SocialInboxMessage
                        message={message}
                        key={message.id}
                        onPostClick={handlePostClick}
                    />
                ))}

                {isLoadMore && (
                    <LoadMoreInfinite loadMore={loadMore} isLoadingMore={isLoadingMore} />
                )}
            </>
        );
    };

    return (
        <div className={clsx('grow space-y-2 overflow-y-scroll p-px pl-1 pr-4 pt-1', className)}>
            {renderMessages()}

            {renderMediaModal()}
        </div>
    );
};
